import { Alert, AlertDescription, AlertTitle, Button } from '@devizy/ui';
import { Briefcase, Buildings, PaperPlaneRight, Storefront } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { VerificationStatusBanner } from '@/components/auth/verification-status-banner';
import { DashCard } from '@/components/dashboard/dash-card';
import { MetricCard } from '@/components/dashboard/metric-card';
import { QuickActionLink } from '@/components/dashboard/quick-action-link';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/dashboard/bidder/company')({
  component: BidderCompanyDashboardPage,
});

function BidderCompanyDashboardPage(): React.ReactElement {
  const { data: profile } = trpc.bidder.me.useQuery();
  const { data: summary, isLoading } = trpc.bidder.dashboardSummary.useQuery();
  const incomplete = !profile?.legalIdentifier;
  const verificationStatus = profile?.verificationStatus ?? null;
  const metric = (value: number | undefined): string =>
    isLoading || value === undefined ? '—' : String(value);
  const showcaseValue =
    isLoading || summary === undefined ? '—' : summary.showcasePublished ? 'Publiée' : 'Inactive';
  const showcaseHref = summary?.showcaseSlug ? `/agency/${summary.showcaseSlug}` : undefined;

  return (
    <>
      {incomplete ? (
        <Alert>
          <AlertTitle>Complétez le profil de votre société</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>Renseignez les informations légales de votre société pour pouvoir postuler.</p>
            <Button asChild>
              <Link to="/profile/bidder/company">Compléter le profil société</Link>
            </Button>
          </AlertDescription>
        </Alert>
      ) : verificationStatus ? (
        <VerificationStatusBanner
          status={verificationStatus}
          kind="COMPANY"
          reason={profile?.verificationRejectionReason ?? null}
        />
      ) : null}

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <MetricCard
          label="Candidatures actives"
          value={metric(summary?.activeBids)}
          loading={isLoading}
          icon={<PaperPlaneRight className="size-5" weight="regular" />}
        />
        <MetricCard
          label="Projets en cours"
          value={metric(summary?.projectsInProgress)}
          loading={isLoading}
          icon={<Briefcase className="size-5" weight="regular" />}
        />
        <MetricCard
          label="Page vitrine"
          value={showcaseValue}
          loading={isLoading}
          href={showcaseHref}
          icon={<Storefront className="size-5" weight="regular" />}
        />
      </section>

      <DashCard
        icon={<Buildings className="size-5" weight="regular" />}
        title="Société"
        subtitle="Renforce la crédibilité de ton profil société"
      >
        <div className="flex flex-col gap-2">
          <QuickActionLink
            href="/profile/bidder/company"
            icon={<Buildings className="size-5" weight="regular" />}
            label="Compléter le profil société"
            hint="Références, équipe et secteurs"
          />
          <QuickActionLink
            href="/settings/plan"
            icon={<Storefront className="size-5" weight="regular" />}
            label="Activer la page vitrine"
            hint="Incluse dans le plan Business"
          />
        </div>
      </DashCard>
    </>
  );
}
